import React from 'react';

import { I18n, translate } from '@fiverr-private/i18n-react';
import { Typography, Link } from '@fiverr-private/typography';
import { Divider, Stack, Container } from '@fiverr-private/layout_components';
import { Tooltip } from '@fiverr-private/tooltip';
import { Button } from '@fiverr-private/button';
import { logger } from '@fiverr-private/obs';
import { useResizableRef } from '@fiverr-private/legal_documents_package';
import { useComplianceContext } from '../../ComplianceProvider/context';
import { calculateEstimationDate, formatDate, helpers } from '../../../utils';
import { RequestStatuses } from '../../../types/complianceTypes';
import { ComplianceTypes } from '../../ComplianceModal/types';
import { getBackgroundCheckStatus, getBackgroundCheckStatusTooltip } from '../../ComplianceProvider/utils';
import { useSellerConfigData } from '../../../api/useSellerConfigData';
import { reportEventsApi } from '../../../biEvents';
import { useComplianceApi } from '../../../hooks/useComplianceApi';
import { DEFAULT_BC_TYPE } from '../../../constants';
import { CollapsibleComplianceTemplate } from './CollapsibleComplianceTemplate';

const ESTIMATE_DAYS = 12;

export const BackgroundCheckCollapsibleSection = ({
    requeseteeId,
    requeseteeUserName,
}: {
    requeseteeId: number;
    requeseteeUserName: string;
}) => {
    const context = useComplianceContext();
    const { ref, compact } = useResizableRef();
    const { canDownloadBackgroundCheckFile, backgroundCheckRequestorUserName, backgroundCheckPackageData } =
        useSellerConfigData(requeseteeId);
    const { getBackgroundCheckResultFile } = useComplianceApi();

    if (!context) return null;

    const sellerComplianceState = context.getSellerComplianceState(requeseteeId);
    const backgroundCheckData = sellerComplianceState.backgroundCheckData;
    const externalVendorId = backgroundCheckData?.externalVendorId;
    const requestStatus = backgroundCheckData?.requestStatus as RequestStatuses;
    const isWithBackgroundCheckAction = [
        RequestStatuses.COMPLETED,
        RequestStatuses.FAILED,
        RequestStatuses.EXPIRED,
    ].includes(requestStatus);
    const visibleStatus = getBackgroundCheckStatus(backgroundCheckData);
    const statusTooltip = getBackgroundCheckStatusTooltip(requeseteeUserName, backgroundCheckData);
    const isWithEstimatedDate = [RequestStatuses.IN_PROGRESS, RequestStatuses.REQUESTED].includes(requestStatus);
    const updatedAt = backgroundCheckData?.updatedAt;
    const lastStatusHistoryUpdateTimeStamp = updatedAt ? new Date(updatedAt).getTime() : undefined;
    const lastStatusDisplayDate = lastStatusHistoryUpdateTimeStamp ? new Date(lastStatusHistoryUpdateTimeStamp) : '';
    const deliveryDate = formatDate(
        isWithEstimatedDate
            ? calculateEstimationDate(lastStatusHistoryUpdateTimeStamp, ESTIMATE_DAYS)
            : lastStatusDisplayDate
    );

    return (
        <CollapsibleComplianceTemplate
            complianceType={ComplianceTypes.BACKGROUND_CHECK}
            status={visibleStatus}
            date={deliveryDate}
        >
            <Divider />
            <Stack paddingX={compact ? '4' : '5'} direction={'column'} gap={'3'} paddingY={'4'} ref={ref}>
                <Stack justifyContent={'spaceBetween'}>
                    <Typography size={'b_sm'}>
                        <I18n k={`compliance.compliance_section.background_check.search_type`} />
                    </Typography>
                    <Tooltip
                        content={translate(
                            `compliance.modal.staticPackage.discription.${
                                backgroundCheckPackageData?.typeId || DEFAULT_BC_TYPE
                            }`
                        )}
                        position="bottom"
                    >
                        <Typography textAlign={'end'} color={'grey_1200'} size={'b_sm'}>
                            <I18n
                                k={`compliance.modal.staticPackage.packageType.${
                                    backgroundCheckPackageData?.typeId || DEFAULT_BC_TYPE
                                }`}
                            />
                        </Typography>
                    </Tooltip>
                </Stack>
                {backgroundCheckRequestorUserName && (
                    <Stack justifyContent={'spaceBetween'}>
                        <Typography fontSize="b_sm">
                            <I18n k={`compliance.compliance_section.background_check.requestor`} />
                        </Typography>
                        <Typography textAlign={'end'} color={'grey_1200'} fontSize="b_sm">
                            {sellerComplianceState.isBackgroundCheckRequestorYou ? (
                                <I18n k={'compliance.compliance_section.background_check.you'} />
                            ) : (
                                backgroundCheckRequestorUserName
                            )}
                        </Typography>
                    </Stack>
                )}
                <Stack justifyContent={'spaceBetween'}>
                    <Typography fontSize="b_sm">
                        <I18n k={`compliance.compliance_section.background_check.status`} />
                    </Typography>
                    <Stack gap="2">
                        <Tooltip position="bottom" content={statusTooltip}>
                            {visibleStatus}
                        </Tooltip>
                        {canDownloadBackgroundCheckFile && (
                            <Link
                                target="_blank"
                                fontWeight="normal"
                                cursor="pointer"
                                size={'b_sm'}
                                onClick={async () => {
                                    try {
                                        if (!!externalVendorId && canDownloadBackgroundCheckFile) {
                                            const fileUrl = (await getBackgroundCheckResultFile({
                                                externalVendorId,
                                            })) as unknown as string;
                                            if (fileUrl) {
                                                helpers.openPageTab(fileUrl);
                                            }
                                        }
                                    } catch (error) {
                                        logger.error(error as Error, {
                                            error,
                                        });
                                    }
                                }}
                            >
                                <I18n k={`compliance.compliance_section.getReport`} />
                            </Link>
                        )}
                    </Stack>
                </Stack>
                <Stack justifyContent={'spaceBetween'}>
                    <Typography fontSize="b_sm">
                        <I18n
                            k={`compliance.compliance_section.background_check.${
                                isWithEstimatedDate ? 'deliveryDateEstimated' : 'deliveryDate'
                            }`}
                        />
                    </Typography>
                    <Typography color={'grey_1200'} fontSize="b_sm">
                        {deliveryDate}
                    </Typography>
                </Stack>
            </Stack>
            {isWithBackgroundCheckAction && (
                <Container>
                    <Divider />
                    <Stack paddingX={'5'} alignItems={'center'} justifyContent={'flexEnd'} paddingY={'4'}>
                        <Button
                            size="sm"
                            variant="outline"
                            intent="secondary"
                            onClick={() => {
                                reportEventsApi.clickedOnBackgroundCheck({
                                    pageName: context.pageName,
                                    sellerId: requeseteeId,
                                    type: 'section',
                                });

                                context?.openBackgroundCheckModal({
                                    sellerId: requeseteeId,
                                    sellerName: requeseteeUserName,
                                });
                            }}
                        >
                            <I18n k={`compliance.compliance_section.action`} />
                        </Button>
                    </Stack>
                </Container>
            )}
        </CollapsibleComplianceTemplate>
    );
};
